import { motion } from "framer-motion";
import { Download, Star } from "lucide-react";
import { portStyle, formatDownloads } from "@/lib/api";
import { TitleAnimation } from "@/components/TitleAnimation";

export const ModCard = ({ mod, index = 0, onDownload, onView }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.4) }}
      data-testid={`mod-card-${mod.id}`}
      onClick={() => onView(mod)}
      className="group relative flex flex-col bg-[#18161d] border border-[#2e2a36] rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:border-[#ef4444]/60 hover:shadow-[0_0_22px_rgba(239,68,68,0.18)]"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        {mod.cover_image ? (
          <img
            src={mod.cover_image}
            alt={mod.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-[linear-gradient(135deg,#1a0e0e_0%,#0a0a0c_50%,#0e1a12_100%)] relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-20"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg,#ef4444 0 2px,transparent 2px 10px)",
              }}
            />
            <span className="relative font-pixel text-sm text-center text-[#84cc16] px-4 leading-snug drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]">
              {mod.title}
            </span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#18161d] via-[#18161d]/10 to-transparent" />

        <span
          data-testid={`mod-category-${mod.id}`}
          className={`absolute top-2.5 left-2.5 px-2 py-1 rounded bg-[#0a0a0c]/80 backdrop-blur-sm border font-mono text-[9px] tracking-widest uppercase ${
            mod.source === "auto"
              ? "border-[#f59e0b] text-[#f59e0b]"
              : "border-[#2e2a36] text-[#84cc16]"
          }`}
        >
          {mod.source === "auto" ? "New" : mod.category}
        </span>

        {mod.rating > 0 && (
          <span className="absolute top-2.5 right-2.5 flex items-center gap-1 px-2 py-1 rounded bg-[#0a0a0c]/80 backdrop-blur-sm border border-[#2e2a36] font-mono text-[10px] text-[#f59e0b]">
            <Star className="w-3 h-3 fill-[#f59e0b]" />
            {mod.rating.toFixed(1)}
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-4">
        <h3 className="font-pixel text-sm text-white tracking-tight mb-1 group-hover:text-[#ef4444] transition-colors flex items-center gap-2">
          <TitleAnimation mod={mod} size={22} />
          <span>{mod.title}</span>
        </h3>
        <p className="text-[11px] font-mono text-gray-500 mb-2">
          by {mod.author} · {mod.year}
        </p>
        <p className="text-xs text-gray-400 font-body leading-relaxed line-clamp-2 mb-3 flex-1">
          {mod.tagline}
        </p>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {mod.source_ports.slice(0, 3).map((p) => {
            const s = portStyle(p);
            return (
              <span
                key={p}
                className="px-1.5 py-0.5 rounded font-mono text-[9px] tracking-wider uppercase border"
                style={{ background: s.bg, borderColor: s.border, color: s.text }}
              >
                {p}
              </span>
            );
          })}
        </div>

        <div className="flex items-center justify-between mt-auto pt-2 border-t border-[#2e2a36]">
          <span className="flex items-center gap-1 text-[11px] font-mono text-gray-500">
            <Download className="w-3 h-3" />
            {formatDownloads(mod.downloads)}
          </span>
          <button
            data-testid={`mod-card-download-btn-${mod.id}`}
            onClick={(e) => {
              e.stopPropagation();
              onDownload(mod);
            }}
            className="inline-flex items-center gap-1.5 bg-[#ef4444]/10 hover:bg-[#ef4444] border border-[#ef4444]/40 hover:border-[#ef4444] text-[#f87171] hover:text-white font-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded transition-all"
          >
            <Download className="w-3 h-3" />
            Get
          </button>
        </div>
      </div>
    </motion.div>
  );
};
